import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { apiUrl } from '../../../environments/environment';
import { Note } from '../../modules/projects/story/notes/notes.component';
import {
  AcceptanceCriterion, Ambiguity,
  AnalysisContent, ConsolidationGroup,
  DataParametersResponse, ExtractedSubstring,
  StoryDescriptionResponse, SupportiveAnalysisContent,
  TokenCount
} from '../templateGen/templateInterfaces';
import { CriteriaGroup } from '../../modules/projects/template/template.component';
import { TestIdService, TestWithMetadata } from '../../modules/projects/test/test-id.service';
import { SupportFile } from '../../modules/projects/story/story.component';

import { DynamicRefinedTestCase } from '../templateGen/templateInterfaces';
import {
  MacroScenarioItem,
  MacroScenarioMicroSpecState
} from '../../modules/projects/template/three-column-workspace/three-column-workspace.component';
import { Constraint } from '../templateGen/template-api.service';
import { RequirementItem } from '../../modules/projects/template/trace-matrix/trace-matrix.component';

const DEFAULT_CONFIG: ModelThinkingConfig = {
  controlType: 'none'
};

export interface TestGenerationProgress {
  totalCriteria: number;
  completedCriteria: number;
  results: any[];
}

export interface GenerateTestsParams {
  criterionText: string;
  index: number;
  scenarioInfo?: {
    id: string;
    name: string;
  };
}

// Interface for the parameters sent to the backend
export interface InferenceParams {
  model: string;
  temperature: number;
  top_p: number;
  thinking_budget?: number;
  thinking_level?: 'low' | 'high';
}

// Interface for the configuration specific of a model
export interface ModelThinkingConfig {
  controlType: 'budget' | 'level' | 'none'; // Tipo di controllo da mostrare
  // Per 'budget'
  minBudget?: number;
  maxBudget?: number;
  step?: number;
  canBeDisabled?: boolean; // Per la levetta "Dynamic"
  // Per 'level'
  levels?: ('low' | 'high')[];
}

@Injectable({
  providedIn: 'root'
})
export class StatoSessioneService {

  private readonly MODEL_CONFIGS: Record<string, ModelThinkingConfig> = {
    // OpenAI
    'GPT-5.4': { controlType: 'none' },
    'GPT-5.4 mini': { controlType: 'none' },
    'GPT-5': { controlType: 'none' },
    'GPT-4.1': { controlType: 'none' },
    // Google
    'Gemini 3': { controlType: 'level', levels: ['low', 'high'] },
    'Gemini 3 flash': { controlType: 'level', levels: ['low', 'high'] },
    'Gemini 2.5 flash': { controlType: 'budget', minBudget: 0, maxBudget: 24576, step: 1, canBeDisabled: true },
    'Gemini 2.5 pro': { controlType: 'budget', minBudget: 128, maxBudget: 32768, step: 1, canBeDisabled: false },
    // Anthropic
    'Claude Opus 4.6': { controlType: 'none' },
    'Claude Sonnet 4.6': { controlType: 'none' },
    'Claude Haiku 4.5': { controlType: 'none' }
  };

  private models = new BehaviorSubject<string[]>([]);
  public models$ = this.models.asObservable();

  private initialInferenceParams: InferenceParams = {
    model: 'Gemini 3 flash',
    temperature: 0.2,
    top_p: 0.8,
    thinking_level: 'high'
  };
  private inferenceParams = new BehaviorSubject<InferenceParams>(this.initialInferenceParams);
  public inferenceParams$ = this.inferenceParams.asObservable();
  public selectedModel$: Observable<string> = this.inferenceParams$.pipe(
    map(params => params.model),
    distinctUntilChanged()
  );

  private tokenUsageSubject = new BehaviorSubject<TokenCount>({
    prompt_tokens: 0,
    completion_tokens: 0,
    total_tokens: 0
  });
  tokenUsage$ = this.tokenUsageSubject.asObservable();

  private topNSubject = new BehaviorSubject<number>(10);
  topN$ = this.topNSubject.asObservable();

  private testGenerationBatchSizeSubject = new BehaviorSubject<number>(5);
  testGenerationBatchSize$ = this.testGenerationBatchSizeSubject.asObservable();

  private processedRequirementsSubject = new BehaviorSubject<RequirementItem[]>([]);
  public processedRequirements$ = this.processedRequirementsSubject.asObservable();

  private triggerGenerateTests = new Subject<boolean>();

  private templates = new BehaviorSubject<any[]>([]);
  templates$ = this.templates.asObservable();

  private isAgenticModeSubject = new BehaviorSubject<boolean>(false);
  isAgenticMode$ = this.isAgenticModeSubject.asObservable();

  private activeTabSubject = new BehaviorSubject<'story' | 'testing'>('story');
  activeTab$ = this.activeTabSubject.asObservable();
  setActiveTab(tab: 'story' | 'testing') { this.activeTabSubject.next(tab); }
  getActiveTab(): 'story' | 'testing' { return this.activeTabSubject.getValue(); }

  private lastSavedAtSubject = new BehaviorSubject<Date | null>(null);
  lastSavedAt$ = this.lastSavedAtSubject.asObservable();
  markSaved() { this.lastSavedAtSubject.next(new Date()); }

  private selectedTemplate = new BehaviorSubject<string>('');
  selectedTemplate$ = this.selectedTemplate.asObservable();

  private selectedTestStyle = new BehaviorSubject<string>('');
  selectedTestStyle$ = this.selectedTestStyle.asObservable();

  private extractedSubstringsSubject = new BehaviorSubject<ExtractedSubstring[]>([]);
  extractedSubstrings$ = this.extractedSubstringsSubject.asObservable();

  private istruzioniScriviTest = new BehaviorSubject<string>('');
  istruzioniScriviTest$ = this.istruzioniScriviTest.asObservable();

  private notesAdded = false;

  private additionalContextSubject = new BehaviorSubject<string>('');
  additionalContext$ = this.additionalContextSubject.asObservable();

  fileNameSubject = new BehaviorSubject<string>('');
  fileName$ = this.fileNameSubject.asObservable();

  private testFileNameSubject = new BehaviorSubject<string>('');
  testFileName$ = this.testFileNameSubject.asObservable();

  private refinedStoryStringSubject = new BehaviorSubject<string>('');
  refinedStoryString$ = this.refinedStoryStringSubject.asObservable();

  private generatedTestsSubject = new BehaviorSubject<TestWithMetadata[]>([]);
  generatedTests$ = this.generatedTestsSubject.asObservable();

  private columnsSubject = new BehaviorSubject<string[]>([]);
  columns$ = this.columnsSubject.asObservable();

  private retrievedNotes = new BehaviorSubject<Note[]>([]);
  retrievedNotes$ = this.retrievedNotes.asObservable();

  projectId = new BehaviorSubject<string>('');
  projectId$ = this.projectId.asObservable();

  projectLanguage = new BehaviorSubject<string>('');
  projectLanguage$ = this.projectLanguage.asObservable();

  private supportFilesSubject = new BehaviorSubject<SupportFile[]>([]);
  supportFiles$ = this.supportFilesSubject.asObservable();

  // subject for supportive file contents
  private supportFileContentsSubject = new BehaviorSubject<Map<string, string>>(new Map());
  supportFileContents$ = this.supportFileContentsSubject.asObservable();

  testCount$: Observable<number> = this.generatedTests$.pipe(
    map(tests => tests.length)
  );

  // STATO TEMPLATE COMPONENT
  private criteriaGroupsSubject = new BehaviorSubject<CriteriaGroup[]>([]);
  criteriaGroups$ = this.criteriaGroupsSubject.asObservable();

  private storyDescriptionSubject = new BehaviorSubject<StoryDescriptionResponse | undefined>(undefined);
  storyDescription$ = this.storyDescriptionSubject.asObservable();

  private macroScenariosSubject = new BehaviorSubject<MacroScenarioItem[]>([]);
  macroScenarios$ = this.macroScenariosSubject.asObservable();

  private dataParametersSubject = new BehaviorSubject<DataParametersResponse | undefined>(undefined);
  dataParameters$ = this.dataParametersSubject.asObservable();

  private currentTestParams?: GenerateTestsParams;
  private allCriteria: string[] = [];

  // BehaviorSubjects for analysis data
  coreAnalysisSubject = new BehaviorSubject<AnalysisContent | null>(null);
  coreAnalysis$ = this.coreAnalysisSubject.asObservable();

  requirementsAnalysisSubject = new BehaviorSubject<AnalysisContent | null>(null);
  requirementsAnalysis$ = this.requirementsAnalysisSubject.asObservable();

  private supportiveAnalysisSubject = new BehaviorSubject<SupportiveAnalysisContent | null>(null);
  supportiveAnalysis$ = this.supportiveAnalysisSubject.asObservable();

  // BehaviorSubject for ambiguities
  private ambiguitiesSubject = new BehaviorSubject<Ambiguity[]>([]);
  ambiguities$ = this.ambiguitiesSubject.asObservable();

  private sessionNameSubject = new BehaviorSubject<string>('');
  public sessionName$ = this.sessionNameSubject.asObservable();

  // New Subject for the mutable display name
  private displayNameSubject = new BehaviorSubject<string>('');
  public displayName$ = this.displayNameSubject.asObservable();

  // Derived observable to quickly check if there are ambiguities
  hasAmbiguities$ = this.ambiguitiesSubject.pipe(
    map(ambiguities => ambiguities.length > 0)
  );

  private testGenerationProgress = new BehaviorSubject<TestGenerationProgress>({
    totalCriteria: 0,
    completedCriteria: 0,
    results: []
  });
  testGenerationProgress$ = this.testGenerationProgress.asObservable();

  private microScenarioSpecificationsSubject = new BehaviorSubject<Map<string, MacroScenarioMicroSpecState> | null>(null);
  microScenarioSpecifications$ = this.microScenarioSpecificationsSubject.asObservable();

  private detailedBreakdownsSubject = new BehaviorSubject<Map<string, string> | null>(null);
  detailedBreakdowns$ = this.detailedBreakdownsSubject.asObservable();

  private selectedMicroScenarioSubject = new BehaviorSubject<any | null>(null);
  selectedMicroScenario$ = this.selectedMicroScenarioSubject.asObservable();

  private refinedTestCasesSubject = new BehaviorSubject<Map<string, DynamicRefinedTestCase[]>>(new Map());
  refinedTestCases$ = this.refinedTestCasesSubject.asObservable();

  // Memorizza i gruppi di test identificati dalla prima fase
  private consolidationGroupsSubject = new BehaviorSubject<ConsolidationGroup[]>([]);
  consolidationGroups$ = this.consolidationGroupsSubject.asObservable();

  // Memorizza i test finali dopo che tutti i gruppi sono stati consolidati
  private globalConsolidatedTestsSubject = new BehaviorSubject<DynamicRefinedTestCase[]>([]);
  globalConsolidatedTests$ = this.globalConsolidatedTestsSubject.asObservable();

  // Add a private counter for generating unique IDs
  private _consolidatedIdCounter = 0;

  // SUBJECT PER I VINCOLI
  private extractedConstraintsSubject = new BehaviorSubject<Constraint[]>([]);
  public extractedConstraints$ = this.extractedConstraintsSubject.asObservable();

  private isReadOnlySession = new BehaviorSubject<boolean>(false);
  public isReadOnlySession$ = this.isReadOnlySession.asObservable();

  // --- AGENTIC FLOW STATE ---
  private proposedResolutionsSubject = new BehaviorSubject<any[]>([]);
  public proposedResolutions$ = this.proposedResolutionsSubject.asObservable();

  private executionPlanSubject = new BehaviorSubject<any | null>(null);
  public executionPlan$ = this.executionPlanSubject.asObservable();

  private agentStepMessagesSubject = new BehaviorSubject<Array<{ step: string; status: string; time?: string }>>([]);
  public agentStepMessages$ = this.agentStepMessagesSubject.asObservable();

  private storyHistorySubject = new BehaviorSubject<any[]>([]);
  public storyHistory$ = this.storyHistorySubject.asObservable();

  private clarificationTimelineSubject = new BehaviorSubject<any>(null);
  public clarificationTimeline$ = this.clarificationTimelineSubject.asObservable();

  public currentModelConfig$: Observable<ModelThinkingConfig>;

  constructor(public testIdService: TestIdService, private http: HttpClient) {
    this.currentModelConfig$ = this.inferenceParams$.pipe(
      map(params => this.getModelConfig(params.model)),
      distinctUntilChanged()
    );

    // Carica i modelli disponibili dal backend
    this.http.get<{ models: string[] }>(`${apiUrl}/api/models`).subscribe({
      next: (res) => {
        this.models.next(res.models);
        // Se il modello default non e' nella lista, usa il primo disponibile
        const current = this.inferenceParams.getValue();
        if (!res.models.includes(current.model) && res.models.length > 0) {
          this.inferenceParams.next({ ...current, model: res.models[0] });
        }
      },
      error: () => console.warn('Impossibile caricare modelli dal backend')
    });
  }

  public getDetailedBreakdownsMap(): Map<string, string> | null {
    return this.detailedBreakdownsSubject.getValue();
  }

  resetAll() {
    // console.log('%c[StatoSessioneService] resetAll CALLED', 'color: red; font-weight: bold;');

    // Reset basic string/array subjects
    this.isAgenticModeSubject.next(true);
    this.additionalContextSubject.next('');
    this.fileNameSubject.next('');
    this.testFileNameSubject.next('');
    this.refinedStoryStringSubject.next('');
    this.generatedTestsSubject.next([]);
    this.istruzioniScriviTest.next('');
    this.columnsSubject.next([]);
    // this.templates.next([]); // Review if this should be reset
    this.selectedTemplate.next('');
    this.supportFilesSubject.next([]);
    this.supportFileContentsSubject.next(new Map());
    this.extractedSubstringsSubject.next([]);
    this.topNSubject.next(10);
    this.testGenerationBatchSizeSubject.next(5);
    this.sessionNameSubject.next('');
    this.displayNameSubject.next('');
    this.activeTabSubject.next('story');
    // Reset analysis data
    this.coreAnalysisSubject.next(null);
    this.requirementsAnalysisSubject.next(null);
    this.supportiveAnalysisSubject.next(null);
    this.ambiguitiesSubject.next([]);
    this.processedRequirementsSubject.next([]);
    this.selectedMicroScenarioSubject.next(null);

    // Reset template component state
    this.storyDescriptionSubject.next(undefined);
    this.macroScenariosSubject.next([]); // Ensure macro scenarios are reset
    this.dataParametersSubject.next(undefined);
    this.criteriaGroupsSubject.next([]); // Ensure criteria groups are reset
    this.resetScenarioGeneratorState();

    // Reset test generation state
    this.currentTestParams = undefined;
    this.allCriteria = [];
    this.testGenerationProgress.next({
      totalCriteria: 0,
      completedCriteria: 0,
      results: []
    });
    this.clearGlobalConsolidationState();
    this.refinedTestCasesSubject.next(new Map());
  }

  /**
   * Resets all state related to a specific analysis run.
   * This is used when generating a new analysis to ensure a clean slate,
   * clearing out core analysis, scenarios, tests, and all derived data.
   */
  public resetAnalysisAndDerivedData(): void {
    console.log('%c[StatoSessioneService] resetAnalysisAndDerivedData CALLED', 'color: red; font-weight: bold;');

    // Reset core analysis and ambiguities
    this.updateAnalysisData(null, null, null);
    this.updateAmbiguities([]);

    // Reset template generation outputs
    this.updateStoryDescription(undefined);
    this.updateDataParameters(undefined);
    this.updateCriteriaGroups([]);
    this.updateExtractedConstraints([]);

    // Reset scenario and test generation outputs
    this.updateMacroScenarios([]);
    this.updateMicroScenarioSpecifications(new Map());
    this.updateGeneratedTests([]);
    this.updateDetailedBreakdowns(new Map());
    this.setAllRefinedTestCasesMap(new Map());

    // Reset token usage for the new run
    this.resetTokenUsage();
    this.selectedMicroScenarioSubject.next(null);
  }

  /**
   * NEW METHOD
   * Resets data that is downstream from core analysis (like scenarios and tests)
   * but leaves the analysis and ambiguities themselves intact.
   * This is ideal for when ambiguity resolutions trigger a regeneration of analysis.
   */
  public resetDataDerivedFromAnalysis(): void {
    console.log('%c[StatoSessioneService] resetDataDerivedFromAnalysis CALLED', 'color: orange; font-weight: bold;');

    // Reset template generation outputs
    this.updateStoryDescription(undefined);
    this.updateDataParameters(undefined);
    this.updateCriteriaGroups([]);
    this.updateExtractedConstraints([]);

    // Reset scenario and test generation outputs
    this.updateMacroScenarios([]);
    this.updateMicroScenarioSpecifications(new Map());
    this.updateGeneratedTests([]);
    this.updateDetailedBreakdowns(new Map());
    this.setAllRefinedTestCasesMap(new Map());

    // NOTE: We DO NOT reset the core analysis or ambiguities here.
    // The calling process is responsible for regenerating/updating them.
  }

  resetTests() {
    // Reset basic string/array subjects
    this.generatedTestsSubject.next([]);
    // this.columnsSubject.next([]);
    // this.selectedTemplate.next('');

    // Reset test generation state
    // this.currentTestParams = undefined;
    this.testGenerationProgress.next({
      totalCriteria: 0,
      completedCriteria: 0,
      results: []
    });
  }

  // <--- NUOVO: Metodo per aggiornare il testbook raffinato ---
  updateRefinedTestCases(macroScenarioId: string, testCases: DynamicRefinedTestCase[]): void {
    // console.log(`[StatoSessioneService] Updating refined test cases for macro ${macroScenarioId} with ${testCases.length} items.`);
    const currentMap = this.refinedTestCasesSubject.getValue();
    const newMap = new Map(currentMap);
    newMap.set(macroScenarioId, testCases);
    this.refinedTestCasesSubject.next(newMap);
  }

  // <--- NUOVO: Metodo per ottenere il testbook raffinato per un macro scenario ---
  getRefinedTestCasesForMacro(macroScenarioId: string): Observable<DynamicRefinedTestCase[]> {
    return this.refinedTestCases$.pipe(
      map(refinedTestCasesMap => refinedTestCasesMap.get(macroScenarioId) || [])
    );
  }

  public getMicroScenarioSpecifications(): Map<string, MacroScenarioMicroSpecState> | null {
    return this.microScenarioSpecificationsSubject.getValue();
  }

  // <--- NUOVO: Metodo per ottenere l'intera mappa dei testbook raffinati (valore corrente) ---
  getAllRefinedTestCasesMap(): Map<string, DynamicRefinedTestCase[]> {
    return this.refinedTestCasesSubject.getValue();
  }

  // Method to update token usage
  updateTokenUsage(newTokens: TokenCount): void {
    // console.log('StatoSessioneService: updateTokenUsage received:', newTokens);
    const currentUsage = this.tokenUsageSubject.getValue();
    // console.log('StatoSessioneService: updateTokenUsage currentUsage:', currentUsage);

    // Add validation to ensure newTokens are valid numbers
    const promptTokensToAdd = typeof newTokens.prompt_tokens === 'number' && !isNaN(newTokens.prompt_tokens) ? newTokens.prompt_tokens : 0;
    const completionTokensToAdd = typeof newTokens.completion_tokens === 'number' && !isNaN(newTokens.completion_tokens) ? newTokens.completion_tokens : 0;
    const totalTokensToAdd = typeof newTokens.total_tokens === 'number' && !isNaN(newTokens.total_tokens) ? newTokens.total_tokens : 0;


    const updatedUsage: TokenCount = {
      prompt_tokens: currentUsage.prompt_tokens + promptTokensToAdd,
      completion_tokens: currentUsage.completion_tokens + completionTokensToAdd,
      total_tokens: currentUsage.total_tokens + totalTokensToAdd
    };

    // console.log('StatoSessioneService: updateTokenUsage updatedUsage:', updatedUsage);
    this.tokenUsageSubject.next(updatedUsage);
  }

  /**
   * Get the current token usage
   * @returns Current token usage counters
   */
  getCurrentTokenUsage(): TokenCount {
    return this.tokenUsageSubject.getValue();
  }

  /**
   * Reset token usage to zero
   */
  resetTokenUsage(): void {
    this.tokenUsageSubject.next({
      prompt_tokens: 0,
      completion_tokens: 0,
      total_tokens: 0
    });
  }

  /**
   * Add token usage to the current running total
   * @param newTokens Token usage to add
   */
  addTokenUsage(newTokens: TokenCount): void {
    // console.log('StatoSessioneService: addTokenUsage received:', newTokens);
    const currentUsage = this.tokenUsageSubject.getValue();
    // console.log('StatoSessioneService: addTokenUsage currentUsage:', currentUsage);

    this.tokenUsageSubject.next({
      prompt_tokens: currentUsage.prompt_tokens + newTokens.prompt_tokens,
      completion_tokens: currentUsage.completion_tokens + newTokens.completion_tokens,
      total_tokens: currentUsage.total_tokens + newTokens.total_tokens
    });
    // console.log('StatoSessioneService: addTokenUsage new subject value:', this.tokenUsageSubject.getValue());
  }

  /**
   * Sets the token usage to a specific value, overwriting the current count.
   * Useful for loading a session from persistence.
   * @param newCount The exact token count to set.
   */
  public setTokenUsage(newCount: TokenCount): void {
    this.tokenUsageSubject.next(newCount);
  }

  public getSelectedTemplate(): string { // New getter
    return this.selectedTemplate.getValue();
  }

  public getTemplates(): any[] {
    return this.templates.getValue();
  }

  public getSelectedTestStyle(): string { // New getter
    return this.selectedTestStyle.getValue();
  }

  public getIstruzioniScriviTest(): string { // New getter
    return this.istruzioniScriviTest.getValue();
  }

  updateMicroScenarioSpecifications(data: Map<string, MacroScenarioMicroSpecState> | null): void {
    // console.log('StatoSessioneService: Updating microScenarioSpecifications', data ? data.size : null);
    this.microScenarioSpecificationsSubject.next(data);
  }

  updateDetailedBreakdowns(data: Map<string, string> | null): void {
    // console.log('StatoSessioneService: Updating detailedBreakdowns', data ? data.size : null);
    this.detailedBreakdownsSubject.next(data);
  }

  resetScenarioGeneratorState(): void {
    // console.log('StatoSessioneService: Resetting Scenario Generator specific state');
    // this.macroScenariosSubject.next([]); // Already handled by resetAll now
    this.microScenarioSpecificationsSubject.next(null);
    this.detailedBreakdownsSubject.next(null);
  }

  // Method for Component A to call
  getCurrentTestParams(): GenerateTestsParams | undefined {
    return this.currentTestParams;
  }

  getNextTestParams(): GenerateTestsParams | undefined {
    if (!this.currentTestParams) { return undefined; }

    const progress = this.testGenerationProgress.value;
    const nextIndex = this.currentTestParams.index + 1;

    // Check if we have more criteria to process
    if (nextIndex < progress.totalCriteria) {
      // Return params for next criterion
      return {
        criterionText: this.allCriteria[nextIndex],
        index: nextIndex
      };
    }

    return undefined;
  }

  initiateGenerateTests() {
    this.triggerGenerateTests.next(true);
  }

  initiateGenerateTestsWithParams(params: GenerateTestsParams) {
    this.currentTestParams = params;
    this.triggerGenerateTests.next(true);
  }

  public updateSessionName(newName: string): void {
    // Only allow setting it if it's empty, or if you explicitly allow overwrites (e.g., loading from DB)
    // If you want strict immutability from the UI side:
    if (this.sessionNameSubject.getValue() === '' || newName === '') {
      this.sessionNameSubject.next(newName);
    } else {
      // If it's already set, we might silently ignore or log a warning
      // ensuring the UI uses updateDisplayName instead.
      console.warn('Attempted to change immutable sessionName. Use updateDisplayName instead.');
    }
  }

  public getSessionName(): string {
    return this.sessionNameSubject.getValue();
  }

  // Method to update display name
  public updateDisplayName(newName: string): void {
    if (this.displayNameSubject.getValue() !== newName) {
      this.displayNameSubject.next(newName);
      // Optional: Update filename based on Display Name if you want files to match the UI title
      // this.updateFileName(newName);
    }
  }

  public getDisplayName(): string {
    return this.displayNameSubject.getValue();
  }

  public updateSelectedMicroScenario(spec: any): void {
    this.selectedMicroScenarioSubject.next(spec);
  }

  getTopN(): number {
    return this.topNSubject.getValue();
  }

  updateTopN(topN: number): void {
    this.topNSubject.next(topN);
  }

  getTestGenerationBatchSize(): number {
    return this.testGenerationBatchSizeSubject.getValue();
  }

  updateTestGenerationBatchSize(batchSize: number): void {
    this.testGenerationBatchSizeSubject.next(batchSize);
  }

  updateDataParameters(dataParameters: DataParametersResponse | undefined) {
    // If the incoming data is undefined, just pass it along and return.
    if (!dataParameters) {
      this.dataParametersSubject.next(undefined);
      return;
    }

    // --- THIS IS THE FIX ---
    // Create a deep copy of the incoming object to create a new reference.
    // This forces the BehaviorSubject to emit a new value to all subscribers.
    const newDataParameters = JSON.parse(JSON.stringify(dataParameters));
    // --- END OF FIX ---


    // Ensure backward compatibility by adding presence flags if they don't exist
    // IMPORTANT: Perform these checks on the NEWLY copied object.
    if (newDataParameters.analysis_results && !newDataParameters.analysis_results.hasOwnProperty('has_config_parameters')) {
      // Check if the configuration parameters exist and have items
      const hasConfigParams =
        newDataParameters.analysis_results.configuration_parameters &&
        newDataParameters.analysis_results.configuration_parameters.length > 0;

      // Check if test data management exists and has any items in its arrays
      const hasTestDataMgmt =
        newDataParameters.analysis_results.test_data_management &&
        (
          (newDataParameters.analysis_results.test_data_management.base_dataset && newDataParameters.analysis_results.test_data_management.base_dataset.length > 0) ||
          (newDataParameters.analysis_results.test_data_management.dynamic_data && newDataParameters.analysis_results.test_data_management.dynamic_data.length > 0) ||
          (newDataParameters.analysis_results.test_data_management.sensitive_data && newDataParameters.analysis_results.test_data_management.sensitive_data.length > 0) ||
          (newDataParameters.analysis_results.test_data_management.edge_cases && newDataParameters.analysis_results.test_data_management.edge_cases.length > 0)
        );

      // Add the presence flags to the analysis results
      newDataParameters.analysis_results = {
        ...newDataParameters.analysis_results,
        has_config_parameters: hasConfigParams,
        has_test_data_management: hasTestDataMgmt,
        has_base_dataset: newDataParameters.analysis_results.test_data_management?.base_dataset?.length > 0,
        has_dynamic_data: newDataParameters.analysis_results.test_data_management?.dynamic_data?.length > 0,
        has_sensitive_data: newDataParameters.analysis_results.test_data_management?.sensitive_data?.length > 0,
        has_edge_cases: newDataParameters.analysis_results.test_data_management?.edge_cases?.length > 0
      };
    }

    // Update the subject with the NEW, modified data parameters object
    this.dataParametersSubject.next(newDataParameters);

    // If you want to update token usage when data parameters are received
    if (newDataParameters.token_usage) {
      this.updateTokenUsage(newDataParameters.token_usage);
    }
  }

  getDataParameters(): DataParametersResponse | undefined {
    return this.dataParametersSubject.getValue();
  }

  public updateGeneratedTests(tests: TestWithMetadata[]): void {
    // *** THIS IS THE NEW CENTRAL LOGIC ***
    // Use the TestIdService to sort and apply global sequential IDs to the entire test suite.
    const finalProcessedTests = this.testIdService.sortAndSequenceTestsByType(tests);

    // Sync the "ID" field in the data object with the front-end calculated displayId.
    // This ensures consistency across the UI and the Session Chat context.
    finalProcessedTests.forEach(test => {
      if (test.data && test.metadata?.displayId) {
        test.data['ID'] = test.metadata.displayId;
      }
    });

    // Now, update the BehaviorSubject with the perfectly prepared array.
    this.generatedTestsSubject.next(finalProcessedTests);

    // Update columns based on the new data
    if (finalProcessedTests && finalProcessedTests.length > 0) {
      this.columnsSubject.next(Object.keys(finalProcessedTests[0].data));
    } else {
      this.columnsSubject.next([]);
    }
  }

  updateSupportFiles(files: SupportFile[]): void {
    this.supportFilesSubject.next(files);
  }

  removeSupportFileByName(fileName: string): void {
    // Remove from file list
    const updatedFiles = this.supportFilesSubject.getValue().filter(f => f.name !== fileName);
    this.supportFilesSubject.next(updatedFiles);

    // Remove from contents map
    const newContents = new Map(this.supportFileContentsSubject.getValue());
    newContents.delete(fileName);
    this.supportFileContentsSubject.next(newContents);

    // Rebuild story: if no files left, strip all appended content
    const baseStory = this.refinedStoryStringSubject.getValue().split(`\n\n'`)[0];
    let newStory = baseStory;
    for (const file of updatedFiles) {
      const content = newContents.get(file.name);
      if (content !== undefined) {
        newStory += `\n\n'${file.name}':\n${content}`;
      }
    }
    this.refinedStoryStringSubject.next(newStory);
  }

  /**
   * Returns the current array of support files
   */
  getSupportFiles(): SupportFile[] {
    return this.supportFilesSubject.getValue();
  }

  // method to update a specific support file's content
  updateSupportFileContent(fileName: string, content: string): void {
    // 1. Update the contents map for individual file viewers
    const currentContents = this.supportFileContentsSubject.getValue();
    const newContents = new Map(currentContents);
    newContents.set(fileName, content);
    this.supportFileContentsSubject.next(newContents);

    // 2. NEW: Trigger the reconstruction of the main refinedStoryString
    this._rebuildAndPropagateRefinedStory();
  }

  /**
   * [PRIVATE] Reconstructs the main refined story by combining the base story
   * with the most up-to-date contents of all support files.
   * This ensures that an edit in any support file is reflected in the main story.
   */
  private _rebuildAndPropagateRefinedStory(): void {
    // Get the current full story and the map of all support file contents
    const fullStory = this.refinedStoryStringSubject.getValue();
    const supportContentsMap = this.supportFileContentsSubject.getValue();

    // If there's no map or it's empty, there's nothing to rebuild.
    if (supportContentsMap.size === 0) {
      return;
    }

    // --- Isolate the "base" story ---
    // We find the base story by splitting the full string at the point where the
    // first support file's content begins. This is robust and handles an empty base story.
    const fileContentMarker = `\n\n'`;
    const baseStory = fullStory.split(fileContentMarker)[0];

    // --- Re-concatenate the story ---
    let newStory = baseStory;
    const allSupportFiles = this.getSupportFiles();

    // Loop through the support files IN THE ORDER THEY WERE ADDED to maintain consistency.
    for (const file of allSupportFiles) {
      // Get the most recent content for this file from our map.
      const fileContent = supportContentsMap.get(file.name);

      // If content exists for this file, append it.
      if (fileContent !== undefined) {
        newStory += `\n\n'${file.name}':\n${fileContent}`;
      }
    }

    // 3. Push the newly constructed story to the main subject.
    // We check if it has actually changed to prevent unnecessary emissions and loops.
    if (newStory !== fullStory) {
      this.refinedStoryStringSubject.next(newStory);
    }
  }

  public getModelConfig(modelName: string): ModelThinkingConfig {
    return this.MODEL_CONFIGS[modelName] || DEFAULT_CONFIG;
  }

  // --- SEMPLIFICATO: Questo metodo deve solo aggiornare lo stato. ---
  // La logica complessa è gestita dal componente prima di chiamare questo metodo.
  public updateInferenceParams(params: Partial<InferenceParams>): void {
    const currentParams = this.inferenceParams.getValue();
    const newParams = { ...currentParams, ...params };
    this.inferenceParams.next(newParams);
  }

  // Get current inference params synchronously
  public getCurrentInferenceParams(): InferenceParams {
    return this.inferenceParams.getValue();
  }

  // getter method for project language
  get getSelectedLanguage(): string {
    return this.projectLanguage.getValue();
  }


  updateTemplates(templates: any[]) {
    const currentTemplates = this.templates.getValue();
    /*console.log('Template update:', {
      previousCount: currentTemplates.length,
      newCount: templates.length,
      previousTemplates: currentTemplates.map(t => t.templateName),
      newTemplates: templates.map(t => t.templateName)
    });*/
    this.templates.next(templates);
  }

  updateSelectedTemplate(selectedTemplate: string) {
    this.selectedTemplate.next(selectedTemplate);
  }

  updateRetrievedNotes(notes: Note[]) {
    this.retrievedNotes.next(notes);
  }

  public updateProjectId(projectId: string, preventFullReset: boolean = false): void {
    // console.log(`%cStatoSessioneService: updateProjectId called with ID: ${projectId}, preventFullReset: ${preventFullReset}`, 'color: magenta;');
    if (this.projectId.getValue() !== projectId || !preventFullReset) { // Resetta solo se l'ID cambia O se il reset non è prevenuto
      if (!preventFullReset) {
        // console.log('%cStatoSessioneService: updateProjectId triggering resetAll() because preventFullReset is false or ID changed significantly without prevention.', 'color: magenta; font-weight: bold;');
        this.resetAll();
      } else {
        // console.log('%cStatoSessioneService: updateProjectId - Full reset PREVENTED for ID:', projectId, 'color: magenta;');
      }
      this.projectId.next(projectId);
    } else {
      //console.log(`%cStatoSessioneService: updateProjectId - Project ID ${projectId} is the same and reset is prevented. No full reset.`, 'color: magenta;');
      // Potrebbe essere necessario emettere .next(projectId) anche qui se qualche componente si affida all'emissione
      // ma senza il resetAll. Tuttavia, se l'ID è lo stesso, non è strettamente necessario.
      // Per sicurezza, puoi fare this.projectId.next(projectId) per assicurare l'emissione.
      if (this.projectId.getValue() !== projectId) this.projectId.next(projectId);
    }
  }

  updateProjectLanguage(language: string) {
    this.projectLanguage.next(language);
  }

  updateIstruzioniScriviTest(istruzioniScriviTest: string) {
    this.istruzioniScriviTest.next(istruzioniScriviTest);
  }

  private getCurrentDateString(): string {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}_${month}_${year}`;
  }

  private updateDateInFileName(fileName: string): string {
    const dateString = this.getCurrentDateString();
    // Regular expression to match the date pattern
    const datePattern = /\d{2}_\d{2}_\d{4}/;

    if (datePattern.test(fileName)) {
      // If a date exists, replace it
      return fileName.replace(datePattern, dateString);
    } else {
      // If no date exists, add it before the file extension or at the end
      const parts = fileName.split('.');
      if (parts.length > 1) {
        const extension = parts.pop();
        return `${parts.join('.')}_${dateString}.${extension}`;
      }
      return `${fileName}_${dateString}`;
    }
  }

  updateFileName(fileName: string) {
    if (fileName === '') {
      this.fileNameSubject.next(fileName);
      this.updateTestFileName(fileName);
    } else {
      const baseFileName = this.removeExtensionAndSuffix(fileName, 'Story');
      const updatedFileName = this.addSuffixIfMissing(baseFileName, '_Story');
      const fileNameWithDate = this.updateDateInFileName(updatedFileName);
      this.fileNameSubject.next(fileNameWithDate);
      this.updateTestFileName(baseFileName);
    }
  }

  updateTestFileName(fileName: string) {
    if (fileName === '') {
      this.testFileNameSubject.next(fileName);
    } else {
      const baseFileName = this.removeExtensionAndSuffix(fileName, 'Tests');
      const updatedFileName = this.addSuffixIfMissing(baseFileName, '_Tests');
      const fileNameWithDate = this.updateDateInFileName(updatedFileName);
      this.testFileNameSubject.next(fileNameWithDate);
    }
  }

  private removeExtensionAndSuffix(fileName: string, suffix: string): string {
    // Remove file extension
    const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
    // Remove suffix if it exists
    const suffixRegex = new RegExp(`${suffix}$`);
    return nameWithoutExtension.replace(suffixRegex, '');
  }

  private addSuffixIfMissing(fileName: string, suffix: string): string {
    return fileName.endsWith(suffix) ? fileName : `${fileName}${suffix}`;
  }

  updateRefinedStoryString(refinedStory: string) {
    this.refinedStoryStringSubject.next(refinedStory);
  }

  updateAdditionalContext(additionalContext: string) {
    this.additionalContextSubject.next(additionalContext);

    const refinedStoryValue = this.refinedStoryStringSubject.getValue();
    if (refinedStoryValue) {
      let refinedStory = refinedStoryValue;

      if (!this.notesAdded) {
        refinedStory += '\n\nNotes:\n';
        this.notesAdded = true;
      }

      // Update the refined story string with the new additional context
      refinedStory = refinedStory.split('\n\nNotes:\n')[0] + '\n\nNotes:\n' + additionalContext;
      //console.log('DEBUG T - stai cercando di aggiornare la refined story con additional context ! ')
      // this.updateRefinedStoryString(refinedStory);
    }
  }

  // Update the entire criteria groups
  updateCriteriaGroups(groups: CriteriaGroup[]) {
    this.criteriaGroupsSubject.next(groups);
  }

  updateCriterion(groupIndex: number, criterionIndex: number, updatedCriterion: AcceptanceCriterion) {
    const currentGroups = [...this.criteriaGroupsSubject.getValue()];

    if (!currentGroups[groupIndex]) {
      console.error('Group not found');
      return;
    }

    // Create new references all the way down
    currentGroups[groupIndex] = {
      ...currentGroups[groupIndex],
      acceptance_criteria: [...currentGroups[groupIndex].acceptance_criteria]
    };

    currentGroups[groupIndex].acceptance_criteria[criterionIndex] = { ...updatedCriterion };

    // Debug log
    /*console.log('Updating criterion:', {
      groupIndex,
      criterionIndex,
      criterion: updatedCriterion,
      newGroups: currentGroups
    });*/

    // Emit the new value
    this.criteriaGroupsSubject.next(currentGroups);
  }

  updateStoryDescription(description: StoryDescriptionResponse | undefined) {
    this.storyDescriptionSubject.next(description);
  }

  updateMacroScenarios(scenarios: MacroScenarioItem[]) {
    this.macroScenariosSubject.next(scenarios);
  }

  // agentic scenario gen
  updateAgenticMode(isAgentic: boolean) {
    this.isAgenticModeSubject.next(isAgentic);
  }

  getCurrentGeneratedTests(): any[] {
    return this.generatedTestsSubject.getValue();
  }

  /**
   * Updates a single test in the list immutably.
   * @param updatedTest The test object with the new data.
   */
  updateTest(updatedTest: TestWithMetadata): void {
    if (!updatedTest?.metadata?.displayId) {
      console.error('Cannot update a test without a valid displayId', updatedTest);
      return;
    }

    const currentTests = this.generatedTestsSubject.getValue();

    // Create a new array by mapping over the old one. This is the "immutable" part.
    // If a test matches the ID, we return the updated version.
    // Otherwise, we return the original, unchanged test.
    const updatedTests = currentTests.map(test =>
      test.metadata.displayId === updatedTest.metadata.displayId
        ? updatedTest
        : test
    );

    // Call the main update method. This will re-calculate sequential IDs if needed
    // and emit the new, updated array to all subscribers.
    this.updateGeneratedTests(updatedTests);
    // console.log(`[StatoSessioneService] Updated test ${updatedTest.metadata.displayId}.`);
  }


  deleteTest(testId: string): void {
    // const identifier = this.testIdService.parseTestId(testId);
    // if (!identifier) { return; }

    const currentTests = this.generatedTestsSubject.getValue();
    const updatedTests = currentTests.filter(test =>
      test.metadata.displayId !== testId
    );

    this.updateGeneratedTests(updatedTests);
  }

  getTestsForGroup(groupIndex: number): Observable<TestWithMetadata[]> {
    return this.generatedTests$.pipe(
      // map(tests => this.testIdService.getGroupTests(tests, groupIndex))
    );
  }

  // Method to update ambiguities
  updateAmbiguities(ambiguities: Ambiguity[]) {
    console.log(`[AmbiguitiesAlertComponent] Received update from ambiguities$. Count: ${ambiguities.length}`);
    this.ambiguitiesSubject.next(ambiguities);
  }

  public updateAnalysisData(
    coreAnalysis: AnalysisContent | null,
    requirementsAnalysis: AnalysisContent | null,
    supportiveAnalysis: SupportiveAnalysisContent | null,
    // ADD THIS NEW, OPTIONAL PARAMETER
    ambiguities?: Ambiguity[]
  ) {
    console.log('%c[StatoSessioneService] updateAnalysisData CALLED.', 'color: blue; font-weight: bold;');
    this.coreAnalysisSubject.next(coreAnalysis);
    this.requirementsAnalysisSubject.next(requirementsAnalysis);
    this.supportiveAnalysisSubject.next(supportiveAnalysis);

    // If ambiguities are provided in this call, update them.
    // If they are not provided (undefined), DO NOTHING to the existing ambiguities.
    if (typeof ambiguities !== 'undefined') {
      console.log(`%c  -> Also updating ambiguities with ${ambiguities.length} items.`, 'color: blue;');
      this.ambiguitiesSubject.next(ambiguities);
    } else {
      console.log('%c  -> Ambiguities were not provided, leaving them as they are.', 'color: blue;');
    }
  }

  /**
   * Get the current ambiguities list
   * @returns Current array of ambiguities
   */
  getAmbiguities(): Ambiguity[] {
    return this.ambiguitiesSubject.getValue();
  }

  /**
   * Get the current criteria groups
   * @returns Current array of criteria groups
   */
  getCriteriaGroups(): CriteriaGroup[] {
    return this.criteriaGroupsSubject.getValue();
  }

  /**
   * Get core analysis data
   * @returns Current core analysis object or null
   */
  getCoreAnalysis(): AnalysisContent | null {
    return this.coreAnalysisSubject.getValue();
  }

  getCoreAnalysisSTR(): string | null {
    return this.coreAnalysisSubject.getValue().scenario_analysis;
  }

  /**
   * Get requirements analysis data
   * @returns Current requirements analysis object or null
   */
  getRequirementsAnalysis(): AnalysisContent | null {
    return this.requirementsAnalysisSubject.getValue();
  }

  public getCurrentMacroScenarios(): MacroScenarioItem[] {
    return this.macroScenariosSubject.getValue();
  }

  /**
   * Get user story data
   * @returns Current story string object or null
   */
  getUserStory(): string | null {
    return this.refinedStoryStringSubject.getValue();
  }

  /**
   * Get supportive analysis data
   * @returns Current supportive analysis object or null
   */
  getSupportiveAnalysis(): SupportiveAnalysisContent | null {
    return this.supportiveAnalysisSubject.getValue();
  }

  public setAllRefinedTestCasesMap(map: Map<string, DynamicRefinedTestCase[]>): void {
    // console.log(`[StatoSessioneService] Setting all refined test cases map with ${map.size} entries.`);
    this.refinedTestCasesSubject.next(map);
  }

  /**
   * Updates the requirements analysis text and propagates the change.
   * This is used by the trace matrix for inline editing, adding, or deleting requirements.
   * @param newText The full, new requirements text, with each requirement on a new line.
   */
  public updateRequirementsAnalysisText(newText: string): void {
    // 1. Get the current value from the subject.
    const currentAnalysisState = this.requirementsAnalysisSubject.getValue();

    // 2. Create a new state object to ensure change detection is triggered correctly.
    //    We handle both cases: where an analysis object already exists, and where it might be null.
    const newAnalysisState: AnalysisContent = {
      // Use the spread operator on the existing state. If it's null, `...{}` is used, which is safe.
      ...(currentAnalysisState || {}),

      // Override or set the scenario_analysis property with the new text.
      scenario_analysis: newText,

      // Ensure the other required property (`config_testdata_analysis`) has a value
      // if the original state was null. Otherwise, keep its existing value.
      config_testdata_analysis: currentAnalysisState?.config_testdata_analysis || ''
    };

    // 3. Push the new state object to the subject.
    //    Any component subscribed to `requirementsAnalysis$` will receive this new value.
    this.requirementsAnalysisSubject.next(newAnalysisState);

    // console.log('%c[StatoSessioneService] Requirements analysis text updated.', 'color: green; font-weight: bold;');
  }

  /**
   * Updates a single test in the global consolidated list immutably.
   * @param updatedTest The test object from the UI, in TestWithMetadata format.
   */
  public updateGlobalConsolidatedTest(updatedTest: TestWithMetadata): void {
    const currentTests = this.globalConsolidatedTestsSubject.getValue();
    const testIndex = currentTests.findIndex(t => t.id === updatedTest.metadata.displayId);

    if (testIndex !== -1) {
      const newTests = [...currentTests];

      // Transform the UI data back into the service's internal format (DynamicRefinedTestCase)
      // We merge with the existing test to preserve any fields not present in the UI form (like 'rationale').
      newTests[testIndex] = {
        ...newTests[testIndex], // Preserve original fields
        id: updatedTest.metadata.displayId,
        data: updatedTest.data,
        // Update top-level properties from the 'data' object for consistency
        title: updatedTest.data['TITLE'] || newTests[testIndex].title,
        type: updatedTest.data['TYPE'] || newTests[testIndex].type
      };

      this.globalConsolidatedTestsSubject.next(newTests);
      // console.log(`[StatoSessioneService] Updated global consolidated test with ID: ${updatedTest.metadata.displayId}`);
    } else {
      console.warn(`[StatoSessioneService] Could not find global consolidated test with ID ${updatedTest.metadata.displayId} to update.`);
    }
  }

  /**
   * Deletes a single test from the global consolidated list by its ID.
   * @param testId The unique ID of the test to delete.
   */
  public deleteGlobalConsolidatedTest(testId: string): void {
    const currentTests = this.globalConsolidatedTestsSubject.getValue();
    const newTests = currentTests.filter(t => t.id !== testId);

    if (newTests.length < currentTests.length) {
      this.globalConsolidatedTestsSubject.next(newTests);
      // console.log(`[StatoSessioneService] Deleted global consolidated test with ID: ${testId}`);
    } else {
      console.warn(`[StatoSessioneService] Could not find global consolidated test with ID ${testId} to delete.`);
    }
  }

  // --- NUOVI METODI PER GESTIRE LA CONSOLIDAZIONE GLOBALE ---

  public updateConsolidationGroups(groups: ConsolidationGroup[]): void {
    // console.log(`[StatoSessioneService] Updating consolidation groups with ${groups.length} groups.`);
    this.consolidationGroupsSubject.next(groups);
  }

  public getConsolidationGroups(): ConsolidationGroup[] {
    return this.consolidationGroupsSubject.getValue();
  }

  public updateGlobalConsolidatedTests(tests: DynamicRefinedTestCase[]): void {
    // console.log(`[StatoSessioneService] Setting global consolidated tests with ${tests.length} tests.`);
    this.globalConsolidatedTestsSubject.next(tests);
  }

  public addGlobalConsolidatedTests(newTests: DynamicRefinedTestCase[]): void {
    if (!newTests || newTests.length === 0) {
      return;
    }
    const currentTests = this.globalConsolidatedTestsSubject.getValue();

    // Map over the incoming tests and assign a new, guaranteed unique ID.
    // We'll use a prefix 'FCTC-' for "Final Consolidated Test Case".
    const processedNewTests = newTests.map(test => {
      const uniqueId = `FCTC-${this._consolidatedIdCounter++}`;
      return {
        ...test, // Keep all original data
        id: uniqueId // OVERWRITE the potentially duplicated ID with our unique one
      };
    });

    const updatedTests = [...currentTests, ...processedNewTests];
    // console.log(`[StatoSessioneService] Appending ${processedNewTests.length} tests with new unique IDs. Total is now ${updatedTests.length}.`);
    this.globalConsolidatedTestsSubject.next(updatedTests);
  }

  public getGlobalConsolidatedTests(): DynamicRefinedTestCase[] {
    return this.globalConsolidatedTestsSubject.getValue();
  }

  // MODIFIED: Reset the counter when the session is reset
  public clearGlobalConsolidationState(): void {
    // console.log('[StatoSessioneService] Clearing global consolidation state.');
    this.consolidationGroupsSubject.next([]);
    this.globalConsolidatedTestsSubject.next([]);
    this._consolidatedIdCounter = 0; // Reset the counter here
  }

  public getFileName(): string {
    return this.fileNameSubject.getValue();
  }

  public getProjectId(): string {
    return this.projectId.getValue();
  }

  updateExtractedConstraints(constraints: Constraint[]): void {
    this.extractedConstraintsSubject.next(constraints);
  }

  getExtractedConstraints(): Constraint[] {
    return this.extractedConstraintsSubject.getValue();
  }

  // method to get only the descriptions
  getConstraintDescriptions(): string[] {
    // 1. Get the full array of Constraint objects
    const allConstraints = this.getExtractedConstraints();

    // 2. Use .map() to create a new array containing only the description property
    return allConstraints.map(constraint => constraint.description);
  }

  public setReadOnly(isReadOnly: boolean): void {
    this.isReadOnlySession.next(isReadOnly);
  }

  /**
   * Updates the centrally-stored list of processed requirements from the Trace Matrix.
   * @param requirements The full, calculated list of requirements with their coverage status.
   */
  public updateProcessedRequirements(requirements: RequirementItem[]): void {
    this.processedRequirementsSubject.next(requirements);
  }

  /**
   * Synchronously gets the current list of processed requirements.
   * @returns The current RequirementItem array.
   */
  public getProcessedRequirements(): RequirementItem[] {
    return this.processedRequirementsSubject.getValue();
  }

  // --- AGENTIC FLOW METHODS ---

  public updateProposedResolutions(resolutions: any[]): void {
    this.proposedResolutionsSubject.next(resolutions);
  }

  public getProposedResolutions(): any[] {
    return this.proposedResolutionsSubject.getValue();
  }

  public updateExecutionPlan(plan: any | null): void {
    this.executionPlanSubject.next(plan);
  }

  public getExecutionPlan(): any | null {
    return this.executionPlanSubject.getValue();
  }

  public updateAgentStepMessages(messages: Array<{ step: string; status: string; time?: string }>): void {
    this.agentStepMessagesSubject.next(messages);
  }

  public getAgentStepMessages(): Array<{ step: string; status: string; time?: string }> {
    return this.agentStepMessagesSubject.getValue();
  }

  public updateStoryHistory(history: any[]): void {
    this.storyHistorySubject.next(history);
  }

  public getStoryHistory(): any[] {
    return this.storyHistorySubject.getValue();
  }

  public updateClarificationTimeline(data: any): void {
    this.clarificationTimelineSubject.next(data);
  }

  public getClarificationTimeline(): any {
    return this.clarificationTimelineSubject.getValue();
  }

}
